<template>
    <div class="dashboard-filter">
        <div class="dashboard-filter-address">
            <template v-if="getSelectedTab !== 'templates'">
                <label for="address-search-input" class="visually-hidden">Søg efter vejnavn</label>
                <input type="text" id="address-search-input" placeholder="Søg efter vejnavn..." autocomplete="off" role="combobox" aria-autocomplete="list" aria-controls="nautocomplete-211-list" aria-expanded="false" style="appearance: none; padding-left: 1em; padding-right: 1em;" ref="dawa" :value="getFilters.address" @keypress.enter.prevent="addressesSet" @click.stop/>
            </template>
        </div>

        <div class="buttons-group">
            <div style="display: inline-block; position: relative;" v-clickoutside="closeFilterlist">
                <button class="button-blue button-blue--small" @click="toggleFilterlist()">
                    <icon class="smaller-svg" name="filter" />
                    Filtre
                </button>

                <transition name="filter-list">
                    <div class="dashboard-filter-list" v-show="showFilterList">
                        <div class="flexbox">
                            <div class="dashboard-filter-heading">Filtre</div>
                            <button class="button-link" @click="resetFilters(selectedTab); resetInputs();">Nulstil filtre</button>
                        </div>

                        <template v-if="filteringList && currentTabSavedFilters.length">
                            <hr />
                            <div class="dashboard-filter-subheading"><label for="filtering-list">Gemte filtreringer</label></div>
                            <select id="filtering-list" ref="filteringList" @change="filteringSet($event)" @click.stop>
                                <option value="" selected disabled>Vælg gemt filtrering...</option>
                                <option v-for="filtering in currentTabSavedFilters" :key="filtering.id" :value="filtering.id">{{ filtering.name }}</option>
                            </select>
                        </template>

                        <template v-if="supplyTypes && supplyTypes.length > 1">
                            <hr />

                            <div class="dashboard-filter-subheading">Forretningsområde</div>
                            <div class="dashboard-filter-group">
                                <div class="dashboard-filter-item" v-for="supplyType in supplyTypes" :key="supplyType.sysBusinessAreaTypeId" :class="'dashboard-filter-item--' + supplyType.systemName">
                                    <input type="checkbox" :name="'supply-type-' + supplyType.systemName" :id="'supply-type-' + supplyType.systemName" :checked="getFilters.supplyType.includes(supplyType.sysBusinessAreaTypeId)" :value="supplyType.sysBusinessAreaTypeId" @change="supplyTypeSet($event)" />
                                    <label :for="'supply-type-' + supplyType.systemName"><icon class="smaller-svg" :name="supplyType.systemName" />{{ supplyType.description }}</label>
                                </div>
                            </div>
                        </template>

                        <hr v-if="getSelectedTab === 'requests' || getSelectedTab === 'tasks' || getSelectedTab === 'requisitions' || getSelectedTab === 'routines'" />

                        <div class="dashboard-filter-columns" v-if="getSelectedTab === 'requests'">
                            <div>
                                <div class="dashboard-filter-subheading"><label for="enquiry-responsible">Ansvarlig</label></div>
                                <n-filter ref="enquiryResponsible" :list="caseworkers" valueKey="userId" labelKey="name" :showSearch="true" :showCounter="true" @filterChanged="enquiryResponsibleChanged" searchPlaceholder="Søg efter ansvarlig">
                                    Ansvarlig
                                    <template v-slot:buttonIcon>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24">
                                            <polygon points="0,0 32,0 16,24" style="fill: rgb(138, 138, 138)"/>
                                        </svg>
                                    </template>
                                </n-filter>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="enquiry-problem-type">Problemtype</label></div>
                                <select id="enquiry-problem-type" ref="enquiryProblemType" key="enquiryProblemType" @change="enquiryProblemTypeSet($event)" @click.stop>
                                    <option value="" selected disabled>Vælg problemtype...</option>
                                    <optgroup v-for="supplyType in supplyTypes.filter(supplyType => getFilters.supplyType.includes(supplyType.sysBusinessAreaTypeId))" :key="supplyType.sysBusinessAreaTypeId" :label="supplyType.description">
                                        <template v-if="problemTypes[supplyType.systemName]">
                                            <option v-for="problemType in problemTypes[supplyType.systemName].codeList" :key="problemType.typeId" :value="problemType.typeId">{{ problemType.description }}</option>
                                        </template>
                                    </optgroup>
                                </select>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="enquiry-contact-type">Kontakttype</label></div>
                                <select id="enquiry-contact-type" ref="enquiryContactType" key="enquiryContactType" @change="enquiryContactTypeSet($event)" @click.stop>
                                    <option value="" selected disabled>Vælg kontakttype...</option>
                                    <optgroup v-for="supplyType in supplyTypes.filter(supplyType => getFilters.supplyType.includes(supplyType.sysBusinessAreaTypeId))" :key="supplyType.sysBusinessAreaTypeId" :label="supplyType.description">
                                        <template v-if="problemTypes[supplyType.systemName]">
                                            <option v-for="contactType in contactTypes[supplyType.systemName].codeList" :key="contactType.typeId" :value="contactType.typeId">{{ contactType.description }}</option>
                                        </template>
                                    </optgroup>
                                </select>
                            </div>
                            <div>
                                <div class="dashboard-filter-subheading">Status</div>
                                <div class="dashboard-filter-item">
                                    <input type="checkbox" name="enquiries-show-closed" id="enquiries-show-closed" v-model="getFilters.enquiries.showClosed" />
                                    <label for="enquiries-show-closed">Vis også afsluttede</label>
                                </div>

                                <hr />

                                <div class="dashboard-filter-subheading">Hændelsesdato</div>
                                <div class="calendar-input">
                                    <base-date-picker id="enquiries-createdDateFrom" :max-date="getFilters.enquiries.createdDate.to" v-model="getFilters.enquiries.createdDate.from">Fra</base-date-picker>
                                    <icon class="calendar small-svg" name="calendar" />
                                </div>
                                <div class="calendar-input">
                                    <base-date-picker id="enquiries-createdDateTo" :min-date="getFilters.enquiries.createdDate.from" v-model="getFilters.enquiries.createdDate.to">Til</base-date-picker>
                                    <icon class="calendar small-svg" name="calendar" />
                                </div>
                            </div>
                        </div>

                        <div class="dashboard-filter-columns" v-if="getSelectedTab === 'tasks'">
                            <div>
                                <div class="dashboard-filter-subheading"><label for="task-responsible">Ansvarlig</label></div>
                                <n-filter ref="taskResponsible" :list="caseworkers" valueKey="userId" labelKey="name" :showSearch="true" :showCounter="true" @filterChanged="taskResponsibleChanged"  searchPlaceholder="Søg efter ansvarlig">
                                    Ansvarlig
                                    <template v-slot:buttonIcon>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24">
                                            <polygon points="0,0 32,0 16,24" style="fill: rgb(138, 138, 138)"/>
                                        </svg>
                                    </template>
                                </n-filter>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="task-company">Virksomhed</label></div>
                                <select id="task-company" ref="taskCompany" key="taskCompany" @change="taskCompanySet($event), taskCompanyChange($event)" @click.stop>
                                    <option value="" selected disabled>Vælg virksomhed...</option>
                                    <option v-for="company in firms" :key="company.id" :value="company.id">{{ company.name }}</option>
                                </select>

                                <div class="dashboard-filter-subheading"><label for="task-contact">Medarbejder</label></div>
                                <select id="task-contact" disabled ref="taskContact" key="taskContact" @change="taskContactSet($event)" @click.stop>
                                    <option value="" selected disabled>Vælg medarbejder...</option>
                                    <option v-for="contact in users" :key="contact.userId" :value="contact.userId">{{ contact.name }}</option>
                                </select>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="tasks-requisitions-serialNumber">Løbenummer (fra aktivitet)</label></div>
                                <input type="search" id="tasks-requisitions-serialNumber" placeholder="Skriv nummer..." ref="taskRequisitionSerialNumberSearch" key="taskRequisitionSerialNumberSearch" v-model="getFilters.tasks.requisitionsSerialNumber" @click.stop/>
                            </div>
                            <div>
                                <div class="dashboard-filter-subheading">Status</div>
                                <div class="dashboard-filter-item">
                                    <input type="checkbox" name="tasks-show" id="tasks-show-closed" v-model="getFilters.tasks.showClosed" />
                                    <label for="tasks-show-closed">Vis også afsluttede</label>
                                </div>
                                <div class="dashboard-filter-item">
                                    <input type="checkbox" name="tasks-show" id="tasks-show-ready-for-approval" v-model="getFilters.tasks.showReadyForApproval" />
                                    <label for="tasks-show-ready-for-approval">Vis kun færdigmeldte</label>
                                </div>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="tasks-name">Opgavenavn</label></div>
                                <input type="search" id="tasks-name" placeholder="Skriv navn..." ref="taskNameSearch" key="taskNameSearch" v-model="getFilters.tasks.name" @click.stop/>

                                <hr />

                                <div class="dashboard-filter-subheading">Startdato</div>
                                <div class="calendar-input">
                                    <base-date-picker id="tasks-startDateFrom" :max-date="getFilters.tasks.startDate.to" v-model="getFilters.tasks.startDate.from">Fra</base-date-picker>
                                    <icon class="calendar small-svg" name="calendar" />
                                </div>
                                <div class="calendar-input">
                                    <base-date-picker id="tasks-startDateTo" :min-date="getFilters.tasks.startDate.from" v-model="getFilters.tasks.startDate.to">Til</base-date-picker>
                                    <icon class="calendar small-svg" name="calendar" />
                                </div>
                            </div>
                        </div>

                        <div class="dashboard-filter-columns" v-if="getSelectedTab === 'requisitions'">
                            <div>
                                <div class="dashboard-filter-subheading"><label for="requisition-responsible">Ansvarlig</label></div>
                                <n-filter ref="requisitionResponsible" :list="caseworkers" valueKey="userId" labelKey="name" :showSearch="true" :showCounter="true" @filterChanged="requisitionResponsibleChanged"  searchPlaceholder="Søg efter ansvarlig">
                                    Ansvarlig
                                    <template v-slot:buttonIcon>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24">
                                            <polygon points="0,0 32,0 16,24" style="fill: rgb(138, 138, 138)"/>
                                        </svg>
                                    </template>
                                </n-filter>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="requisition-company">Virksomhed</label></div>
                                <select id="requisition-company" ref="requisitionCompany" key="requisitionCompany" @change="requisitionCompanySet($event), requisitionCompanyChange($event)" @click.stop>
                                    <option value="" selected disabled>Vælg virksomhed...</option>
                                    <option v-for="company in firms" :key="company.id" :value="company.id">{{ company.name }}</option>
                                </select>

                                <div class="dashboard-filter-subheading"><label for="requisition-contact">Medarbejder</label></div>
                                <select id="requisition-contact" disabled ref="requisitionContact" key="requisitionContact" @change="requisitionContactSet($event)" @click.stop>
                                    <option value="" selected disabled>Vælg medarbejder...</option>
                                    <option v-for="contact in users" :key="contact.userId" :value="contact.userId">{{ contact.name }}</option>
                                </select>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="requisitions-activity-type">Aktivitetstype</label></div>
                                <select id="requisitions-activity-type" ref="requisitionActivityType" key="requisitionActivityType" @change="requisitionActivityTypeSet($event)" @click.stop>
                                    <option value="" selected disabled>Vælg aktivitetstype...</option>
                                    <optgroup v-for="supplyType in supplyTypes.filter(supplyType => getFilters.supplyType.includes(supplyType.sysBusinessAreaTypeId))" :key="supplyType.sysBusinessAreaTypeId" :label="supplyType.description">
                                        <template v-if="activityTypes[supplyType.systemName]">
                                            <option v-for="activityType in activityTypes[supplyType.systemName].codeList" :key="activityType.typeId" :value="activityType.typeId">{{ activityType.description }}</option>
                                        </template>
                                    </optgroup>
                                </select>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="requisitions-status">Status</label></div>
                                <select id="requisitions-status" ref="requisitionStatusType" key="requisitionStatusType" @change="requisitionStatusTypeSet($event)" @click.stop>
                                    <option value="" selected disabled>Vælg status...</option>
                                    <option v-for="statusType in statusTypes" :key="statusType.systemName" :value="statusType.systemName">{{ statusType.description }}</option>
                                </select>
                            </div>

                            <div>
                                <div class="dashboard-filter-subheading">Status</div>
                                <div class="dashboard-filter-item">
                                    <input type="checkbox" name="requisitions-date-overdue" id="requisitions-date-overdue" v-model="getFilters.requisitions.dateOverdue" />
                                    <label for="requisitions-date-overdue">Vis kun overskredne</label>
                                </div>
                                <div class="dashboard-filter-item">
                                    <input type="checkbox" name="requisitions-show-approved" id="requisitions-show-approved" v-model="getFilters.requisitions.showApproved" />
                                    <label for="requisitions-show-approved">Vis kun godkendte</label>
                                </div>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="requisitions-taskName">Opgavenavn</label></div>
                                <input type="search" id="requisitions-taskName" placeholder="Skriv navn..." ref="requisitionTaskNameSearch" key="requisitionTaskNameSearch" v-model="getFilters.requisitions.name" @click.stop/>

                                <hr />

                                <div class="dashboard-filter-subheading">Startdato</div>
                                <div class="calendar-input">
                                    <base-date-picker id="requisitions-startDateFrom" :max-date="getFilters.requisitions.startDate.to" v-model="getFilters.requisitions.startDate.from">Fra</base-date-picker>
                                    <icon class="calendar small-svg" name="calendar" />
                                </div>
                                <div class="calendar-input">
                                    <base-date-picker id="requisitions-startDateTo" :min-date="getFilters.requisitions.startDate.from" v-model="getFilters.requisitions.startDate.to">Til</base-date-picker>
                                    <icon class="calendar small-svg" name="calendar" />
                                </div>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="requisitions-serialNumber">Løbenummer</label></div>
                                <input type="search" id="requisitions-serialNumber" placeholder="Skriv nummer..." ref="requisitionSerialNumberSearch" key="requisitionSerialNumberSearch" v-model="getFilters.requisitions.serialNumber" @click.stop/>
                            </div>
                        </div>

                        <div class="dashboard-filter-columns" v-if="getSelectedTab === 'routines'">
                            <div>
                                <div class="dashboard-filter-subheading"><label for="routine-responsible">Ansvarlig</label></div>
                                <n-filter ref="routineResponsible" :list="caseworkers" valueKey="userId" labelKey="name" :showSearch="true" :showCounter="true" @filterChanged="routineResponsibleChanged" searchPlaceholder="Søg efter ansvarlig">
                                    Ansvarlig
                                    <template v-slot:buttonIcon>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24">
                                            <polygon points="0,0 32,0 16,24" style="fill: rgb(138, 138, 138)"/>
                                        </svg>
                                    </template>
                                </n-filter>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="routine-company">Virksomhed</label></div>
                                <select id="routine-company" ref="routineCompany" key="routineCompany" @change="routineCompanySet($event)" @click.stop>
                                    <option value="" selected disabled>Vælg virksomhed...</option>
                                    <option v-for="company in firms" :key="company.id" :value="company.id">{{ company.name }}</option>
                                </select>
                            </div>

                            <div>
                                <div class="dashboard-filter-subheading">Status</div>
                                <div class="dashboard-filter-item">
                                    <input type="checkbox" name="routines-show-closed" id="routines-show-closed" v-model="getFilters.routines.showClosed" />
                                    <label for="routines-show-closed">Vis også afsluttede</label>
                                </div>

                                <hr />

                                <div class="dashboard-filter-subheading"><label for="routines-name">Rutinenavn</label></div>
                                <input type="search" id="routines-name" placeholder="Skriv navn..." ref="routineNameSearch" key="routineNameSearch" v-model="getFilters.routines.name" @click.stop/>
                            </div>
                        </div>

                        <template v-if="getSelectedTab === 'templates'">
                            <div>
                                <div class="dashboard-filter-subheading"><label for="templates-name">Skabelonnavn</label></div>
                                <input type="search" id="templates-name" placeholder="Skriv navn..." ref="templateNameSearch" key="templateNameSearch" v-model="getFilters.templates.name" @click.stop/>
                            </div>
                        </template>

                        <template v-if="getSelectedTab === 'requests' || getSelectedTab === 'tasks' || getSelectedTab === 'requisitions' || getSelectedTab === 'routines'">
                            <hr />

                            <div class="flexbox">
                                <button type="button" class="button-green" @click="openModal('dashboard-modal-filter-save')">Gem aktuel filtrering</button>
                                <button type="button" class="button-link" @click="openModal('dashboard-modal-filter-list', { filteringType: currentFilteringType })">Vis gemte filtreringer</button>
                            </div>
                        </template>
                    </div>
                </transition>
            </div>

            <button class="button-blue button-blue--small" v-if="getSelectedTab === 'requests' || getSelectedTab === 'tasks' || getSelectedTab === 'routines'" @click="exportToExcel()">
                <icon class="smaller-svg" name="share" />
                Eksporter
            </button>

            <div style="display: inline-block; position: relative;" v-else-if="getSelectedTab === 'requisitions'" v-clickoutside="closeExportlist">
                <button class="button-blue button-blue--small" aria-haspopup="true" :aria-expanded="showExportList ? 'true' : 'false'" @click="toggleExportlist()">
                    <icon class="smaller-svg" name="share" />
                    Eksporter
                </button>

                <transition name="export-list">
                    <div class="dashboard-export-list" role="menu" v-show="showExportList">
                        <button type="button" role="menuitem" @click="exportToExcel('requisitions_objects')">Pr. aktivitet/objekt</button>
                        <button type="button" role="menuitem" @click="exportToExcel('requisitions')">Pr. opgave</button>
                    </div>
                </transition>
            </div>
        </div>

        <div class="dashboard-filter-simple">
            <div class="dashboard-filter-heading">Filtre</div>

            <div class="dashboard-filter-columns">
                <div v-if="getSelectedTab !== 'templates'">
                    <div class="dashboard-filter-subheading"><label for="address-search-simple">Søg efter vejnavn</label></div>
                    <input type="search" id="address-search-simple" placeholder="Søg efter vejnavn..." autocomplete="off" role="combobox" aria-autocomplete="list" aria-controls="nautocomplete-211-list" aria-expanded="false" style="appearance: none; padding-left: 1em; padding-right: 1em;" ref="dawa" :value="getFilters.address" @keypress.enter.prevent="addressesSet" />
                </div>

                <div>
                    <template v-if="supplyTypes && supplyTypes.length > 1">
                        <div class="dashboard-filter-subheading">Forretningsområde</div>
                        <div class="dashboard-filter-group">
                            <div class="dashboard-filter-item" v-for="supplyType in supplyTypes" :key="supplyType.sysBusinessAreaTypeId" :class="'dashboard-filter-item--' + supplyType.systemName">
                                <input type="checkbox" :name="'supply-type-' + supplyType.systemName" :id="'supply-type-' + supplyType.systemName + '-simple'" :checked="getFilters.supplyType.includes(supplyType.sysBusinessAreaTypeId)" :value="supplyType.sysBusinessAreaTypeId" @change="supplyTypeSet($event)" />
                                <label :for="'supply-type-' + supplyType.systemName + '-simple'"><icon class="smaller-svg" :name="supplyType.systemName" />{{ supplyType.description }}</label>
                            </div>
                        </div>
                    </template>
                    <div class="dashboard-filter-subheading"><label for="requisition-contact-simple">Medarbejder</label></div>
                    <select id="requisition-contact-simple" ref="requisitionContactSingle" key="requisitionContactSingle" :value="getFilters.requisitions.contact[0]" @change="requisitionContactSingleSet($event)">
                        <option value="" selected disabled>Vælg medarbejder...</option>
                        <option value="">Alle</option>
                        <option v-for="contact in users" :key="contact.userId" :value="contact.userId">{{ contact.name }}</option>
                    </select>
                </div>
            </div>
        </div>

        <modal v-if="showModal" :type="modalType" :data="modalData" @close="closeModal" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import axios from '@/store/api.js'
import NFilter from '@/components/base/NFilter.vue'
import clickoutside from '@/mixins/shared/clickoutside.js'
import modal from '@/components/shared/modal/modal.vue'
import BaseDatePicker from '@/components/base/BaseDatePicker.vue'

export default {
    name: 'dashboard-filters',
    directives: {
        clickoutside
    },
    components: {
        NFilter,
        modal,
        BaseDatePicker
    },
    data () {
        return {
            modalData: null,
            modalType: null,
            showModal: false,

            showFilterList: false,
            showExportList: false,

            filters: {},
            filteringList: null
        }
    },
    computed: {
        ...mapGetters({
            userInfo: 'userInfo',
            getSelectedTab: 'getSelectedTab',

            // Filters
            supplyTypes: 'getBusinessAreaTypes',
            caseworkers: 'caseWorkerList',
            problemTypes: 'getProblemTypes',
            contactTypes: 'getCClientTypeList',
            firms: 'getAllFirmsForActivity',
            users: 'getAllUsersInFirmForActivity',
            activityTypes: 'getCActivityTypeList',
            statusTypes: 'getSysRequisitionAndReportStatusList',
            getFilters: 'getFilters'
        }),

        currentFilteringType () {
            return this.getSelectedTab.slice(0, -1)
        },

        currentTabSavedFilters () {
            return this.filteringList.filter(filtering => filtering.type === this.currentFilteringType)
        },

        selectedTab () {
            if (this.getSelectedTab === 'requests') {
                return 'enquiries'
            }
            return this.getSelectedTab
        },

        problemTypeList () {
            const list = []
            const groups = [...this.supplyTypes].filter(supplyType => this.getFilters.supplyType.includes(supplyType.sysBusinessAreaTypeId))
            groups.forEach((group) => {
                if (this.problemTypes[group.systemName]) {
                    list.push(this.problemTypes[group.systemName].codeList)
                }
            })

            return list.flat()
        }
    },
    methods: {
        ...mapActions({
            convertToExcel: 'exportToExcel',
            setFilters: 'setFilters',
            removeFilters: 'removeFilters',
            resetFilters: 'resetFilters',
            getUserList: 'getAssociatedOrganizationUserList',
            setCustomFilter: 'setCustomFilter'
        }),

        resetInputs () {
            if (this.$refs.filteringList) {
                this.$refs.filteringList.selectedIndex = 0
            }
            if (this.getSelectedTab === 'requests') {
                this.$refs.enquiryResponsible.checked = []
                this.$refs.enquiryProblemType.selectedIndex = 0
                this.$refs.enquiryContactType.selectedIndex = 0
            }
            if (this.getSelectedTab === 'tasks') {
                this.$refs.taskResponsible.checked = []
                this.$refs.taskCompany.selectedIndex = 0
                this.$refs.taskContact.selectedIndex = 0
                this.$refs.taskContact.disabled = true
                this.$refs.taskNameSearch.value = ''
            }
            if (this.getSelectedTab === 'requisitions') {
                this.$refs.requisitionResponsible.checked = []
                this.$refs.requisitionCompany.selectedIndex = 0
                this.$refs.requisitionContact.selectedIndex = 0
                this.$refs.requisitionContact.disabled = true
                this.$refs.requisitionActivityType.selectedIndex = 0
                this.$refs.requisitionStatusType.selectedIndex = 0
                this.$refs.requisitionTaskNameSearch.value = ''
            }
            if (this.getSelectedTab === 'routines') {
                this.$refs.routineResponsible.checked = []
                this.$refs.routineCompany.selectedIndex = 0
                this.$refs.routineNameSearch.value = ''
            }
            if (this.getSelectedTab === 'templates') {
            }
        },
        openModal (type, data) {
            this.modalType = type
            this.modalData = data
            this.showModal = true
        },
        closeModal () {
            this.showModal = false
            this.loadFilters()
        },
        loadFilters () {
            axios.get('/Query/GetFilters')
                .then((response) => {
                    this.filteringList = response.data.map((filtering) => {
                        return {
                            id: filtering.filterId,
                            type: filtering.filterType,
                            name: filtering.name,
                            parameterList: JSON.parse(filtering.parametersJson)
                        }
                    })
                })
                .catch((error) => {
                    console.error('error', error)
                })
        },
        exportToExcel (type) {
            this.closeExportlist()
            this.convertToExcel({
                type: type || this.getSelectedTab
            })
        },
        toggleFilterlist () {
            this.showFilterList = !this.showFilterList
        },
        closeFilterlist () {
            this.showFilterList = false
        },
        toggleExportlist () {
            this.showExportList = !this.showExportList
        },
        closeExportlist () {
            this.showExportList = false
        },
        addressesSet (event) {
            this.getFilters.address = event.target.value
        },
        filteringSet (event) {
            const filtering = this.filteringList.find(filtering => filtering.id === event.target.options[event.target.selectedIndex].value)
            this.resetFilters('user')
            this.setCustomFilter(filtering)
            this.showFilterList = false
        },
        supplyTypeSet (event) {
            const supplyTypeId = event.target.value

            if (event.target.checked) {
                this.setFilters({
                    filter: 'supplyType',
                    value: supplyTypeId
                })
            } else {
                if (this.getFilters.supplyType.length === 1) {
                    this.$notify({
                        group: 'info',
                        type: 'warn',
                        text: 'Der skal være valgt minimum et forretningsområde.',
                        duration: 7500
                    })
                    event.target.checked = true
                    return
                }

                this.removeFilters({
                    filter: 'supplyType',
                    value: supplyTypeId
                })
            }
        },
        enquiryResponsibleChanged (array) {
            this.caseworkers.forEach((responsible) => {
                if (array.includes(responsible.userId)) {
                    this.setFilters({
                        page: 'enquiries',
                        filter: 'responsible',
                        value: responsible.userId
                    })
                } else {
                    this.removeFilters({
                        page: 'enquiries',
                        filter: 'responsible',
                        value: responsible.userId
                    })
                }
            })
        },
        enquiryResponsibleReset (event) {
            if (event.target.checked) {
                this.caseworkers.forEach((responsible) => {
                    this.removeFilters({
                        page: 'enquiries',
                        filter: 'responsible',
                        value: responsible.userId
                    })
                })
            }
        },
        enquiryProblemTypeSet (event) {
            const problemTypeId = event.target.value

            this.setFilters({
                page: 'enquiries',
                filter: 'problemType',
                value: problemTypeId
            })
        },
        enquiryContactTypeSet (event) {
            const contactTypeId = event.target.value

            this.setFilters({
                page: 'enquiries',
                filter: 'contactType',
                value: contactTypeId
            })
        },
        taskResponsibleChanged (array) {
            this.caseworkers.forEach((responsible) => {
                if (array.includes(responsible.userId)) {
                    this.setFilters({
                        page: 'tasks',
                        filter: 'responsible',
                        value: responsible.userId
                    })
                } else {
                    this.removeFilters({
                        page: 'tasks',
                        filter: 'responsible',
                        value: responsible.userId
                    })
                }
            })
        },
        taskCompanyChange () {
            this.$refs.taskContact.value = ''
            this.$refs.taskContact.disabled = true
        },
        taskCompanySet (event) {
            const companyId = event.target.value

            this.setFilters({
                page: 'tasks',
                filter: 'company',
                value: companyId
            })

            this.getUserList({
                firmaId: companyId,
                supplyType: this.getFilters.supplyType
            })
                .then((response) => {
                    if (response.length === 0) {
                        this.$refs.taskContact.disabled = true
                    } else {
                        this.$refs.taskContact.disabled = false
                    }
                })
        },
        taskContactSet (event) {
            const contactId = event.target.value

            this.setFilters({
                page: 'tasks',
                filter: 'contact',
                value: contactId
            })
        },
        requisitionResponsibleChanged (array) {
            this.caseworkers.forEach((responsible) => {
                if (array.includes(responsible.userId)) {
                    this.setFilters({
                        page: 'requisitions',
                        filter: 'responsible',
                        value: responsible.userId
                    })
                } else {
                    this.removeFilters({
                        page: 'requisitions',
                        filter: 'responsible',
                        value: responsible.userId
                    })
                }
            })
        },
        requisitionCompanyChange () {
            this.$refs.requisitionContact.value = ''
            this.$refs.requisitionContact.disabled = true
        },
        requisitionCompanySet (event) {
            const companyId = event.target.value

            this.setFilters({
                page: 'requisitions',
                filter: 'company',
                value: companyId
            })

            this.getUserList({
                firmaId: companyId,
                supplyType: this.getFilters.supplyType
            })
                .then((response) => {
                    if (response.length === 0) {
                        this.$refs.requisitionContact.disabled = true
                    } else {
                        this.$refs.requisitionContact.disabled = false
                    }
                })
        },
        requisitionContactSet (event) {
            const contactId = event.target.value

            this.setFilters({
                page: 'requisitions',
                filter: 'contact',
                value: contactId
            })
        },
        requisitionContactSingleSet (event) {
            const contactId = event.target.value

            this.resetFilters('root')
            if (contactId) {
                this.setFilters({
                    page: 'requisitions',
                    filter: 'contact',
                    value: contactId
                })
            }
        },
        requisitionActivityTypeSet (event) {
            const activityTypeId = event.target.value

            this.setFilters({
                page: 'requisitions',
                filter: 'activityType',
                value: activityTypeId
            })
        },
        requisitionStatusTypeSet (event) {
            const statusId = event.target.value

            this.setFilters({
                page: 'requisitions',
                filter: 'statusType',
                value: statusId
            })
        },
        routineResponsibleChanged (array) {
            this.caseworkers.forEach((responsible) => {
                if (array.includes(responsible.userId)) {
                    this.setFilters({
                        page: 'routines',
                        filter: 'responsible',
                        value: responsible.userId
                    })
                } else {
                    this.removeFilters({
                        page: 'routines',
                        filter: 'responsible',
                        value: responsible.userId
                    })
                }
            })
        },
        routineCompanySet (event) {
            const companyId = event.target.value

            this.setFilters({
                page: 'routines',
                filter: 'company',
                value: companyId
            })
        }
    },
    watch: {
        getSelectedTab () {
            this.$nextTick(() => {
                this.resetInputs()
            })
        },
        'getFilters.tasks': {
            deep: true,
            handler () {
                let companyId = null

                if (this.$refs.taskCompany && this.$refs.taskCompany.selectedIndex > 0) {
                    companyId = this.$refs.taskCompany.value
                }
                if (companyId) {
                    this.getUserList({
                        firmaId: companyId,
                        supplyType: this.getFilters.supplyType
                    })
                        .then(() => {
                            if (this.$refs.taskCompany && this.$refs.taskCompany.searchText !== '' && this.$refs.taskContact) {
                                this.$refs.taskContact.disabled = false
                            }
                        })
                }
            }
        },
        'getFilters.requisitions': {
            deep: true,
            handler () {
                let companyId = null

                if (this.$refs.requisitionCompany && this.$refs.requisitionCompany.selectedIndex > 0) {
                    companyId = this.$refs.requisitionCompany.value
                }
                if (companyId) {
                    this.getUserList({
                        firmaId: companyId,
                        supplyType: this.getFilters.supplyType
                    })
                        .then(() => {
                            if (this.$refs.requisitionCompany && this.$refs.requisitionCompany.searchText !== '' && this.$refs.requisitionContact) {
                                this.$refs.requisitionContact.disabled = false
                            }
                        })
                }
            }
        }
    },
    created () {
        this.filters = this.getFilters
    },
    mounted () {
        this.loadFilters()

        if (!this.userInfo.permissions.requests.enabled && !this.userInfo.permissions.tasks.enabled && this.userInfo.permissions.requisitions.enabled) {
            this.getUserList({
                firmaId: this.userInfo.organizationId,
                supplyType: this.getFilters.supplyType
            })
                .then((response) => {
                    this.$refs.requisitionContactSingle.selectedIndex = [...this.$refs.requisitionContactSingle.options].findIndex(option => option.value === this.userInfo.userId)
                })
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/dashboard/dashboard-filters.vue